import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from 'firebase/auth';
import FirebaseAuthService from '../services/firebase-auth.service';

// Interfaz para la información del usuario
export interface UserInfo {
  name: string;
  lastname: string;
  business: number;
  profileImage: string | null;
  email: string;
  token: string | null;
  role?: string; // 'user', 'company', 'admin'
  companyId?: string; // ID de la empresa si el usuario es una empresa
  leadPrice?: number; // Precio por lead para empresas
  id?: string; // ID interno del usuario en el backend
}

// Interfaz para el contexto de autenticación
interface AuthContextType {
  currentUser: User | null;
  userInfo: UserInfo | null;
  loading: boolean;
  signInWithGoogle: (isCompanyCreation?: boolean) => Promise<User>;
  signInAnonymously: () => Promise<User>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  setUserInfo: (info: UserInfo | null) => void;
}

// Crear el contexto
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Props para el proveedor
interface AuthProviderProps {
  children: ReactNode;
}

// Proveedor del contexto de autenticación
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);

  // Efecto para observar cambios en el estado de autenticación
  useEffect(() => {
    const unsubscribe = FirebaseAuthService.onAuthStateChanged((user) => {
      setCurrentUser(user);
      setLoading(false);

      // Si el usuario se desconecta, limpiar la información del usuario
      if (!user) {
        setUserInfo(null);
      }
    });

    // Cargar información del usuario desde localStorage si existe
    const savedUserInfo = localStorage.getItem('user_info');
    if (savedUserInfo) {
      try {
        setUserInfo(JSON.parse(savedUserInfo));
      } catch (error) {
        console.error('Error al cargar la información del usuario desde localStorage:', error);
      }
    }

    // Limpiar el observador al desmontar el componente
    return () => unsubscribe();
  }, []);

  // Iniciar sesión con Google
  const signInWithGoogle = async (isCompanyCreation = false) => {
    return FirebaseAuthService.signInWithGoogle(isCompanyCreation);
  };

  // Iniciar sesión anónima para onboarding
  const signInAnonymously = async () => {
    return FirebaseAuthService.signInAnonymously();
  };

  // Cerrar sesión
  const signOut = async () => {
    // Limpiar la información del usuario al cerrar sesión
    setUserInfo(null);
    localStorage.removeItem('user_info');
    return FirebaseAuthService.signOut();
  };

  // Refrescar el token
  const refreshToken = async () => {
    return FirebaseAuthService.refreshToken();
  };

  // Función para establecer la información del usuario
  const updateUserInfo = (info: UserInfo | null) => {
    setUserInfo(info);
    if (info) {
      localStorage.setItem('user_info', JSON.stringify(info));
    } else {
      localStorage.removeItem('user_info');
    }
  };

  // Valor del contexto
  const value = {
    currentUser,
    userInfo,
    loading,
    signInWithGoogle,
    signInAnonymously,
    signOut,
    refreshToken,
    setUserInfo: updateUserInfo
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
